<template>
    <div class="n2-center-container" style="background-color:#fff; padding:10px; position: relative;">
      <div v-if="!success" style="padding:0;">
        <h4>{{ $t('pages.signup.signup') }}</h4>
        <p v-html="$t('pages.signup.signup_desc')"></p>
        <div style="max-width:600px;">
          <form-input type="text" v-model="contact.given_name" :label="$t('pages.signup.given_name')" :errors="model_errors.given_name"/>
          <form-input type="text" v-model="contact.family_name" :label="$t('pages.signup.family_name')" :errors="model_errors.family_name"/>
          <form-input type="text" v-model="contact.address.address_line_1" :label="$t('pages.signup.address_line_1')" :errors="model_errors.address_line_1"/>
          <form-input type="text" v-model="contact.address.postal_code" :label="$t('pages.signup.postal_code')" :errors="model_errors.postal_code"/>
          <form-input type="text" v-model="contact.address.locality" :label="$t('pages.signup.locality')" :errors="model_errors.locality"/>
  
          <!-- Address country -->
          <div class="n2-form-group">
            <label class="n2-form-label">{{ $t('pages.signup.country_code') }}</label>
            <app-autocomplete
              v-model="contact.address.country_code"
              :items="countries"
              :text_field="'name'"
              :separator="' '"
              :value_field="'code'"
              :errors="model_errors.country_code">
            </app-autocomplete>
          </div>
  
          <!-- Phone number (E.164 via reusable component) -->
          <div class="n2-form-group">
            <label class="n2-form-label">{{ $t('pages.signup.phone_number') }}</label>
            <phone-number-field
              v-model="contact.phone_number"                      
              :defaultCountry="contact.address.country_code || 'NO'"
              :locale="app_locale"
              :errors="{ national: model_errors.phone_number }"
            />
          </div>
  
          <form-input type="text" v-model="contact.email" :label="$t('pages.signup.email')" :errors="model_errors.email"/>
          <form-input type="text" v-model="contact.email_repeat" :label="$t('pages.signup.email_repeat')" :errors="model_errors.email_repeat"/>
          <form-input type="password" v-model="contact.password" :label="$t('pages.signup.password')" :errors="model_errors.password"/>
          <form-input type="password" v-model="contact.password_repeat" :label="$t('pages.signup.password_repeat')" :errors="model_errors.password_repeat"/>
  
          <validation-summary :errors="model_errors"/>
          <div class="n2-margin-top">
            <button class="n2-button-1" @click="signup">{{ $t('pages.signup.signup') }}</button>
          </div>
        </div>
      </div>
  
      <div v-else class="n2-text-center" style="padding:0">
        <h4>{{ $t('pages.signup.signup_success') }}</h4>
        <p>{{ $t('pages.signup.signup_success_desc') }}</p>
      </div>
  
      <app-loader :active="loading"/>
    </div>
  </template>
  
  <script>
  import { mapGetters } from 'vuex'
  import { AccountService } from '../services'
  
  import FormInput from "../components/shared/form-input.vue"
  import ValidationSummary from '../components/shared/validation-summary.vue'
  import AppAutocomplete from '../components/shared/autocomplete.vue'
  import AppLoader from '../components/shared/loader.vue'
  import PhoneNumberField from '../components/app/phone-number-field.vue'
  
  import country_list from '../data/country-list'
  
  export default {
    components: { FormInput, ValidationSummary, AppAutocomplete, AppLoader, PhoneNumberField },
    data () {
      return {
        loading: false,
        model_errors: {},
        success: false,
        contact: {
          given_name: '',
          family_name: '',
          email: '',
          email_repeat: '',
          password: '',
          password_repeat: '',
          phone_number: '',              // E.164，由组件维护
          address: {
            address_line_1: '',
            postal_code: '',
            locality: '',
            country_code: 'NO'
          }
        }
      }
    },
    computed: {
      ...mapGetters('globalization', ['app_locale', 'locale']),
      countries () {
        return this.app_locale === 'no' ? country_list.no : country_list.en
      }
    },
    created () {
      this.accountService = new AccountService()
    },
    methods: {
      validateForm () {
        this.model_errors = {}
        if (!this.contact.given_name) {
          this.model_errors['given_name'] = [this.$t('pages.signup.error_given_name')]
        }
        if (!this.contact.family_name) {
          this.model_errors['family_name'] = [this.$t('pages.signup.error_family_name')]
        }
        if (!this.contact.address.address_line_1) {
          this.model_errors['address_line_1'] = [this.$t('pages.signup.error_address_line_1')]
        }
        if (!this.contact.address.postal_code) {
          this.model_errors['postal_code'] = [this.$t('pages.signup.error_postal_code')]
        }
        if (!this.contact.address.locality) {
          this.model_errors['locality'] = [this.$t('pages.signup.error_locality')]
        }
        if (!this.contact.address.country_code) {
          this.model_errors['country_code'] = [this.$t('pages.signup.error_country_code')]
        }
  
        // Phone：只需有值；格式由 PhoneNumberField 保证为合法 E.164
        if (!this.contact.phone_number) {
          this.model_errors['phone_number'] = [this.$t('pages.signup.error_phone_number')]
        }
  
        const patternEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!patternEmail.test(this.contact.email || '')) {
          this.model_errors['email'] = [this.$t('pages.signup.error_email')]
        }
        const strongRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})")
        if (!strongRegex.test(this.contact.password || '')) {
          this.model_errors['password'] = [this.$t('pages.signup.error_password')]
        }
        if ((this.contact.email || '').toLowerCase().trim() !== (this.contact.email_repeat || '').toLowerCase().trim()) {
          this.model_errors['email_repeat'] = [this.$t('pages.signup.error_email_repeat')]
        }
        if ((this.contact.password || '') !== (this.contact.password_repeat || '')) {
          this.model_errors['password_repeat'] = [this.$t('pages.signup.error_password_repeat')]
        }
        return Object.keys(this.model_errors).length === 0
      },
  
      async signup () {
        if (this.contact.email_repeat) this.contact.email_repeat = this.contact.email_repeat.toLowerCase().trim()
        if (this.contact.email) this.contact.email = this.contact.email.toLowerCase().trim()
        // ⚠️ 不要对手机号做 toLowerCase；PhoneNumberField 已输出 E.164
        if (!this.validateForm()) return
  
        try {
          this.loading = true
          this.contact.locale = this.locale
          await this.accountService.create(this.contact)
          this.success = true
          await this.$store.dispatch('identity/signin', {
            email: this.contact.email,
            password: this.contact.password
          })
        } catch (err) {
          if (err?.data) {

            let errorCode = err?.data?.error_code;
            if (errorCode) {
                if(errorCode == 'model_error')
                {
                    if(err.data.errors.phone_number){
                    this.model_errors['phone_number'] = [this.$t('pages.signup.error_phone_number')]
                }}
            }
            console.log(err)
            if (err.data.errors.email) {
              this.$set(this.model_errors, 'email', [this.$t('pages.signup.error_email_exist')])
            }
          }
        } finally {
          this.loading = false
        }
      }
    }
  }
  </script>
  
  <style lang="sass">
  </style>